import React, { useEffect, useState, useMemo, useRef } from 'react';
import uniqBy from 'lodash/uniqBy';
import { fireErrorAnalytics } from '@jira/platform__error-reporting/src/fireErrorAnalytics.tsx';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import log from '@atlassian/jira-common-util-logging/src/log.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import getMeta from '@atlassian/jira-get-meta';
import { fireOperationalAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { RESOURCE_TYPE_NOOP } from '@atlassian/jira-router-resources-utils/src/constants.tsx';
import { useSpaStateActions } from '@atlassian/jira-spa-state-controller/src/common/index.tsx';
import { useSpaStateTransition } from '@atlassian/jira-spa-state-controller/src/components/transition-state/index.tsx';
import {
	createRouterSelector,
	useResource,
	type Match,
	type RouteResource,
	type Route,
} from '@atlassian/react-resource-router';
import { clearPrefetchStartMarks, getPrefetchStartMark } from './utils/prefetch-timing/index.tsx';

type ResourceRequestMetrics = {
	duration: number | null;
	end: number | null;
	isCacheHit: boolean | null;
	start: number;
	type: string | null;
};

type ResourceMetricTrackerProps = {
	isInitialRender: boolean;
	resource: RouteResource<unknown>;
	route: Route;
	currentMatch: Match;
};

const defaultMetrics: ResourceRequestMetrics = {
	type: null,
	duration: null,
	end: null,
	isCacheHit: null,
	start: 0,
};

const canSendMetrics = ({ duration, end, isCacheHit, start }: ResourceRequestMetrics): boolean =>
	isCacheHit !== defaultMetrics.isCacheHit &&
	duration !== defaultMetrics.duration &&
	start !== defaultMetrics.start &&
	end !== defaultMetrics.end;

const isCachedResource = (slice: ReturnType<typeof useResource>, invocationTimestamp: number) =>
	!slice.loading && (slice.expiresAt ?? 0) > invocationTimestamp;

const SAMPLING_RATE = 10;

const ResourceMetricTracker = ({
	resource,
	route: currentRoute,
	currentMatch,
	isInitialRender,
}: ResourceMetricTrackerProps) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { setResourceMetrics } = useSpaStateActions();
	const [metrics, setMetrics] = useState<ResourceRequestMetrics>(defaultMetrics);
	const isSentRef = useRef(false);

	const slice = useResource(resource);

	useEffect(
		() =>
			// clear metrics on route change
			() => {
				setMetrics({ ...defaultMetrics });
				isSentRef.current = false;
			},
		[currentRoute],
	);

	// watch the resource and update metrics
	useEffect(() => {
		if (slice.expiresAt === null || isSentRef.current) {
			// don't record metrics until the slice has an expiry set
			return;
		}

		const invocationTimestamp = Date.now();

		if (metrics.isCacheHit === null && isCachedResource(slice, invocationTimestamp)) {
			// cached resource
			setMetrics((state) => ({
				...state,
				duration: 0,
				end: invocationTimestamp,
				isCacheHit: true,
				start: invocationTimestamp,
				type: resource.type,
			}));
			return;
		}

		if (slice.loading && metrics.start === 0) {
			// set start time
			setMetrics((state) => ({
				...state,
				type: resource.type,
				isCacheHit: false,
				start: invocationTimestamp,
			}));
			return;
		}

		if (
			!slice.loading &&
			metrics.start !== defaultMetrics.start &&
			metrics.end === defaultMetrics.end
		) {
			// set end time
			setMetrics((state) => ({
				...state,
				duration: invocationTimestamp - state.start,
				end: invocationTimestamp,
			}));
		}
	}, [currentRoute, metrics, resource, slice]);

	// watch the metrics object and send event when it has enough data
	useEffect(() => {
		const { error } = slice;

		if (
			isSentRef.current ||
			!canSendMetrics(metrics) ||
			// Always send error events but sample success events (1 in every 10)
			(!error && Math.random() * 100 > SAMPLING_RATE)
		) {
			return;
		}

		const { isCacheHit, duration, start, end, type: resourceType } = metrics;

		const action = error ? 'fetchFailed' : 'fetchSucceeded';
		const analyticsEvent = createAnalyticsEvent({ action });
		const errorMessage = error && error instanceof Error ? error.toString() : error;
		const routeName = currentRoute.name ? currentRoute.name : 'empty';

		fireOperationalAnalytics(analyticsEvent, `${action} resource`, resource.type, {
			duration,
			isCacheHit,
			start,
			end,
			routeName,
			prefetchStartTime: getPrefetchStartMark(currentMatch),
			isInitialRender,
			resourceType,
			...(errorMessage !== null && errorMessage !== undefined ? { error: errorMessage } : {}),
		});

		if (error) {
			fireErrorAnalytics({
				meta: {
					id: `spaTransitionResources.${(resourceType || '').toLowerCase()}`,
					packageName: 'jiraSpaTransitionResourcesTracker',
					teamName: 'nike',
				},
				attributes: {
					// Filter out synthetic tests using all known ways
					isSynthetic: getMeta('ajs-is-synthetic') === 'true' || fg('forge_ui_synthetic_check'),
				},
				// @ts-expect-error - TS2322 - Type 'Error | Record<string, any> | (Error & Record<string, any>) | (Record<string, any> & Error)' is not assignable to type 'Error | undefined'.
				error,
				skipSentry: true,
			});
			log.safeErrorWithoutCustomerData(
				`resources.${(resourceType || '').toLowerCase()}`,
				// @ts-expect-error - TS2345 - Argument of type 'string | Record<string, any>' is not assignable to parameter of type 'string'.
				errorMessage || '',
				error,
			);
		}

		// add the resource metrics to the spa state store
		setResourceMetrics({
			// @ts-expect-error - TS2322 - Type 'number | null' is not assignable to type 'number'.
			end,
			// @ts-expect-error - TS2322 - Type 'boolean | null' is not assignable to type 'boolean'.
			isCacheHit,
			start,
			// @ts-expect-error - TS2322 - Type 'string | null' is not assignable to type 'string'.
			type: resourceType,
		});

		isSentRef.current = true;
	}, [
		createAnalyticsEvent,
		currentRoute,
		metrics,
		setResourceMetrics,
		slice,
		resource,
		currentMatch,
		isInitialRender,
	]);

	return null;
};

// VISIBLE FOR TESTING
export const useRouterRoute = createRouterSelector((router) => router.route);
export const useRouterMatch = createRouterSelector((router) => router.match);

export const SpaResourceMetricsController = () => {
	const currentRoute = useRouterRoute();
	const currentMatch = useRouterMatch();
	const [{ isInitialRender }] = useSpaStateTransition();

	useEffect(() => {
		clearPrefetchStartMarks(currentMatch);
	}, [currentMatch]);

	const resourcesToTrack: RouteResource<unknown>[] = useMemo(
		() =>
			uniqBy(
				currentRoute?.resources?.filter((resource) => resource.type !== RESOURCE_TYPE_NOOP),
				(resource) => resource.type,
			),
		[currentRoute?.resources],
	);

	return resourcesToTrack.map((resource) => (
		<ResourceMetricTracker
			key={resource.type}
			resource={resource}
			route={currentRoute}
			currentMatch={currentMatch}
			isInitialRender={isInitialRender}
		/>
	));
};
